import React, { useState, useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import { IsThereAvatarBanner } from '@/components/IsThereAvatarBanner'
import {
  EmtBreadcrumbs,
  EmtHeader,
  EmtIcon,
  EmtSeparator,
  EmtTypography,
  useMobile,
  EmtButton,
} from 'emt-components-v2'
import { Images, Routes } from '@/assets/configs'
import NextLink from 'next/link'
import Skeleton from '@mui/material/Skeleton'
import {
  MobileHeader,
  ProfileContainer,
  ProfileMap,
  UserImagesList,
  UserImagesListLoading,
  Wrapper,
} from '@/components/Profile/partials'
import { LightBox } from './partials/StyledComponents'
import {
  Availabilities,
  Benefices,
  Experience,
  List,
  Love,
  Similars,
  UserDetails,
} from '@/components/PetSitter/Profile'
import { InfoBox } from '@/components/PetSitter/InfoBox'
import { Info } from '@/components/PetSitter/Info'
import Container from '@mui/material/Container'
import Comments from '@/components/Profile/containers/Comments'
import { ImagesSliderModal } from '@/components/Profile/Modals'
import Footer from '@/components/EMT/Footer'
import MobileSwipeMenu from '@/components/PetSitter/MobileSwipeMenu'
import { MobileSwipeMenuRef } from '@/components/PetSitter/MobileSwipeMenu'
import SignUpModal from '@/components/Modals/SignUpModal'
import { getPetsNumber } from '@/pages/pet-sitter/search'

// TYPES
import { Coordinate } from '@/data'
import { useRouter } from 'next/router'
import { messageSanitize } from '@/helpers/messageSanitize'
import AskSitterCard from '../PetSitter/AskSitterCard'
import VariantImages from '@/assets/BaseVariantImages'
import Calendar from '@/components/pages/pet-sitter/pro/my-services/calendar/Calendar'
// import PetsittersSelection from '@/components/PetSitter/PetsittersSelection/PetsittersSelection'
import PetsittersSelectionButtons from '@/components/PetSitter/PetsittersSelection/PetsittersSelectionButtons'
import { useTranslation } from 'next-i18next'
import { formatPrice } from '@/utils/formatPrice'

// Hooks
import useCurrentUser from '@/hooks/useCurrentUser'
import { usePetsitterProfileData } from '@/hooks/usePetsitterProfileData'
import { useModalScrollLock } from '@/hooks/useModalScrollLock'
import { useImageSlider } from '@/hooks/useImageSlider'

// Store
import { useNewRequestCartStore } from '@/store/newRequestCartStore'

// Data
import { PRODUCTS_MAP } from '@/hooks/usePetsitterPrice'
import { cancellationPoliciesMapping } from '@/data/PetSitter/Messages'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import { PriceIncludedTooltip } from '@/components/PetSitter/PriceIncludedTooltip'

type Props = React.PropsWithChildren<{
  customerId: string
  overrideBackButton?: () => void
  getUserProfileName?: (name?: string) => void
  profile?: any
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}>

const PublicPetSitterProfileComponent: React.FC<Props> = ({
  customerId,
  overrideBackButton,
  getUserProfileName,
  profile,
  scrollContainerRef,
}) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')

  const swipeMenuRef = useRef<MobileSwipeMenuRef>(null)

  const scrollWithOffset = (target: string | HTMLElement | null) => {
    const element = typeof target === 'string' ? document.getElementById(target) : target
    if (!element) return

    const scrollContainer = scrollContainerRef?.current

    if (scrollContainer) {
      const elementRect = element.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()

      const top =
        scrollContainer.scrollTop +
        (elementRect.top - containerRect.top) -
        (isMobile && petsittersSelectionRef.current
          ? petsittersSelectionRef.current.getBoundingClientRect().height + 70
          : 70)

      scrollContainer.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth',
      })
      return
    }

    const rect = element.getBoundingClientRect()
    const absoluteTop = window.scrollY + rect.top

    window.scrollTo({
      top: Math.max(0, absoluteTop - window.innerHeight / 6),
      behavior: 'smooth',
    })
  }

  const [isMobile] = useMobile()

  const { activeModalSlider, setActiveModalSlider, imageClickedIndex, handleImageClick } = useImageSlider()
  const [signUpModalOpen, setSignUpModalOpen] = useState<boolean>(false)
  const petsittersSelectionRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { tendersEnabled, currentUser } = useCurrentUser()

  // Send gtm event when see profile as connected user
  const hasSentProfileEvent = useRef(false)
  useEffect(() => {
    if (!!currentUser || hasSentProfileEvent.current) return
    hasSentProfileEvent.current = true
    gtmSendCustomEvent('member_view_public_profile_petsitter_sst', {
      member_role_variable: 'maitre',
      member_id_variable: 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }, [currentUser])

  const petsToComplete = useNewRequestCartStore((s) => s.petsToComplete)

  useModalScrollLock(activeModalSlider || signUpModalOpen)

  useEffect(() => {
    if (!signUpModalOpen) return

    // Send gtm event when open signup modal
    gtmSendCustomEvent('member_registration_popup_view_sst', {
      member_role_variable: 'maitre',
      member_id_variable: 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }, [signUpModalOpen])

  const disponibilitiesRef = React.useRef<HTMLDivElement>(null)

  const {
    userProfile,
    isLoading,
    reviewsSummary,
    hasMoreReviews,
    reviewsLoadingMore,
    isInitialReviewsLoading,
    loadMoreReviews,
    petsitterPrice,
    reduction,
    quantity,
    allImages,
    statsData,
  } = usePetsitterProfileData({
    customerId,
    profile,
  })

  // Scroll to hash
  useEffect(() => {
    if (!router.isReady) return
    if (!userProfile) return

    if (router.asPath.includes('#reviews')) {
      scrollWithOffset('reviews')
    }
  }, [router.isReady, router.asPath, userProfile])

  return (
    <>
      {/* DESKTOP Header */}
      {!isMobile && !!currentUser && (
        <EmtHeader backgroundColor="#762D8A" background={Images.Header.Pattern}>
          <IsThereAvatarBanner t={tc} showHeader={false} sx={{ paddingBottom: '30px' }} />
          {!isLoading && userProfile ? (
            <EmtBreadcrumbs variant="white">
              <NextLink href={Routes.Home} passHref legacyBehavior>
                <Box sx={{ display: 'flex', gap: '5px', cursor: 'pointer' }}>
                  <EmtIcon className="emt-paw" />
                  {tc('breadcrumbs.home')}
                </Box>
              </NextLink>
              <NextLink
                href={tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base}
                passHref
                legacyBehavior
              >
                PetSitter
              </NextLink>
              <EmtTypography>{userProfile.name}</EmtTypography>
            </EmtBreadcrumbs>
          ) : (
            <Skeleton variant="text" height={24} width="40%" />
          )}
        </EmtHeader>
      )}
      {/* END DESKTOP Header */}
      {/* MOBILE Header */}
      {!isLoading && isMobile && userProfile ? (
        <>
          <IsThereAvatarBanner t={tc} sx={{ paddingTop: '80px' }} />
          <MobileHeader
            picturesUrls={allImages}
            handleImageClick={handleImageClick}
            overrideBackButton={overrideBackButton}
            pro
            userIsNew={!!userProfile?.isNew}
            userLastRefresh={userProfile?.lastRefresh}
            reviewsCount={reviewsSummary?.reviewsCount}
            rating={reviewsSummary?.averageRating}
            distance={userProfile?.distance}
            averageResponseTimeUnderThreeHours={statsData?.averageResponseTimeUnderThreeHours}
          />
        </>
      ) : isMobile ? (
        <Skeleton variant="rectangular" height={320} width="100%" />
      ) : null}
      {/* END MOBILE Header */}
      {/* CONTENT */}
      <Wrapper>
        <Container
          sx={{
            paddingTop: '30px',
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: '1fr 360px',
            alignItems: 'stretch',
            gap: '20px',
          }}
          maxWidth="lg"
        >
          {/* Profile Container */}
          <ProfileContainer>
            {!isLoading && userProfile ? (
              <>
                {/* DESKTOP Images List */}
                {!isMobile && (
                  <UserImagesList
                    picturesUrls={allImages}
                    userIsNew={!!userProfile?.isNew}
                    userLastRefresh={userProfile?.lastRefresh}
                    onClick={(imageIndex) => handleImageClick(imageIndex)}
                    reviewsCount={reviewsSummary?.reviewsCount}
                    rating={reviewsSummary?.averageRating}
                    distance={userProfile?.distance}
                    averageResponseTimeUnderThreeHours={statsData?.averageResponseTimeUnderThreeHours}
                  />
                )}
                {/* END DESKTOP Images list */}
                {/* User Details */}
                <UserDetails
                  firstName={userProfile.name}
                  distance={userProfile.distance}
                  reviewsToCount={reviewsSummary.reviewsCount}
                  averageReviewToRating={reviewsSummary.averageRating}
                  badges={userProfile.badges}
                  plainBadges={userProfile.plainBadges}
                  petsitterTitle={userProfile.title}
                  city={userProfile.city}
                  zipCode={userProfile.zipCode}
                  petsitter
                  onScrollToSection={scrollWithOffset}
                  statsData={statsData}
                  isReviewsLoading={isInitialReviewsLoading}
                />
                {/* END User Details */}

                {/* END Petsitters selection */}
                {/* Experience */}
                <Box id="knowledge-section">
                  <Experience petsitterExperience={userProfile.petsitterExperience} />
                </Box>
                {/* END Experience */}
                {/* Availabilities */}
                <EmtTypography variant="h4" fontWeight={800}>
                  {t('petsitter-profile.isProposing', { name: userProfile.name })}
                </EmtTypography>
                <Availabilities data={userProfile.petsitterService} />
                {/* END Availabilities */}
                {/* Info */}
                <InfoBox
                  iconClass="emt-birthday"
                  title={userProfile.petsitterService?.minAnimalAgeSupport?.title}
                  text={userProfile.petsitterService?.minAnimalAgeSupport?.description}
                />
                {/* END Info */}
                {/* Criteria List */}
                {userProfile.petsitterService?.criteria && <List data={userProfile.petsitterService.criteria} />}
                {/* END Criteria List */}
                {/* Love */}
                <Box id="psychology-section">
                  <Love petsitterPetLoving={userProfile.petsitterPetLoving} name={userProfile.name} />
                </Box>
                {/* END Love */}
                {/* Needs to know */}
                <LightBox sx={{ minHeight: 'unset', padding: '27px', borderColor: '#E8E8EA' }}>
                  <EmtTypography variant="body1" fontWeight="700" fontSize="18px">
                    {t('petsitter-profile.needToKnow', { name: userProfile.name })}
                  </EmtTypography>
                  <EmtTypography variant="body1">
                    {messageSanitize(userProfile.petsitterService.petownerInformationsRequest, {
                      address: false,
                      keywords: false,
                    })}
                  </EmtTypography>
                </LightBox>
                {/* END Needs to know */}
                <EmtSeparator />
                {/* News photos */}
                <Info
                  image={VariantImages.CheckGreen}
                  title={userProfile.petsitterService.takeInformatedPetownerEngagement.title}
                  text={userProfile.petsitterService.takeInformatedPetownerEngagement.description}
                />
                {/* END News photos */}
                <EmtSeparator />
                {/* Cancellation Policy */}
                <InfoBox
                  iconClass="emt-calendar-no-access-1"
                  title={userProfile.petsitterService.cancellationPolicies?.title}
                  text={userProfile.petsitterService.cancellationPolicies?.description}
                />
                {/* END Cancellation Policy */}
                {/* Disponibilities */}
                <div ref={disponibilitiesRef}>
                  <EmtTypography variant="h4" fontWeight={800}>
                    {t('petsitter-profile.disponibilities', { name: userProfile.name })}
                  </EmtTypography>
                  <Calendar id={userProfile.uuid} viewOnly />
                </div>
                {/* END Disponibilities */}

                {/* Location */}
                <EmtTypography variant="h4" fontWeight={800}>
                  {t('petsitter-profile.location', { name: userProfile.name })}
                </EmtTypography>
                {userProfile?.profileHome ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '15px' : '38px' }}>
                      {userProfile?.profileHome?.homeType && (
                        <InfoBox
                          center
                          iconClass={userProfile.profileHome?.homeType.icon.type}
                          title={userProfile.profileHome?.homeType.title}
                          text={userProfile.profileHome?.homeType.description}
                        />
                      )}
                      {userProfile?.profileHome?.homeAreaType && (
                        <InfoBox
                          center
                          iconClass={userProfile.profileHome?.homeAreaType.icon.type}
                          title={userProfile.profileHome?.homeAreaType.title}
                          text={userProfile.profileHome?.homeAreaType.description}
                        />
                      )}
                    </Box>
                    {userProfile?.profileHome?.homeDescription && (
                      <EmtTypography variant="body1">
                        {messageSanitize(userProfile.profileHome?.homeDescription, { address: false, keywords: false })}
                      </EmtTypography>
                    )}
                    <EmtSeparator />
                    <Box sx={{ display: 'flex', gap: '20px' }}>
                      {userProfile.profileHome?.haveNatureProximity && (
                        <Info image={VariantImages.Plant} title={userProfile.profileHome?.haveNatureProximity.title} />
                      )}
                      {userProfile.profileHome?.isTabagist && (
                        <>
                          <EmtSeparator sx={{ height: '100%', width: '1px' }} />
                          <Info image={VariantImages.Nosmoke} title={userProfile.profileHome?.isTabagist.title} />
                        </>
                      )}
                    </Box>
                    {userProfile.profileHome?.homeInformations && (
                      <List data={userProfile.profileHome?.homeInformations} />
                    )}
                  </>
                ) : (
                  <EmtTypography variant="body1">
                    {userProfile.name} ne propose pas de garde à son domicile.
                  </EmtTypography>
                )}
                {/* END Location */}

                {/* Position */}
                <Box id="map-section">
                  <ProfileMap
                    address={
                      {
                        latitude: userProfile?.latitude,
                        longitude: userProfile?.longitude,
                      } as Coordinate
                    }
                    color="purple"
                  />
                </Box>
                {/* END Position */}
                {/* Languages List */}
                {userProfile.languages && <List data={userProfile.languages} />}
                {/* END Languages List */}
                {/* Comments */}
                <Box id="reviews">
                  {userProfile && (
                    <Comments
                      profile={{ ...userProfile, reviewsSummary, id: userProfile.uuid, displayName: userProfile.name }}
                      buttonColor="purple"
                      activeForm={false}
                      hasMoreReviews={hasMoreReviews}
                      reviewsLoadingMore={reviewsLoadingMore}
                      onLoadMoreReviews={loadMoreReviews}
                    />
                  )}
                </Box>
                {/* END Comments */}
              </>
            ) : (
              // Loading state
              <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '30px' }}>
                {!isMobile && <UserImagesListLoading />}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="text" width="100%" height={58} />
                  <Skeleton variant="rectangular" width="100%" height={12} />
                </Box>
                <Box sx={{ display: 'flex', gap: '25px' }}>
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                </Box>
                <Box>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                  <Skeleton variant="text" width="85%" height={20} />
                </Box>
                <Box>
                  <Skeleton variant="rectangular" width="100%" height={260} />
                </Box>
              </Box>
            )}
          </ProfileContainer>
          {/* END Profile Container */}

          {/* Profile Sidebar */}
          {!isMobile && (
            <Box
              sx={{
                minWidth: 0,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  position: 'sticky',
                  top: '20px',
                  maxHeight: `calc(100vh - 120px)`,
                  display: 'flex',
                }}
              >
                {!isLoading && userProfile && (
                  <AskSitterCard
                    data={{
                      ...userProfile,
                      reviewsCount: reviewsSummary.reviewsCount,
                      reviewsRating: reviewsSummary.averageRating,
                    }}
                    onScrollToSection={scrollWithOffset}
                    setSignUpModalOpen={() => setSignUpModalOpen(true)}
                  />
                )}
              </Box>
              {/* END Profile Sidebar */}
            </Box>
          )}
        </Container>
        {/* {!isMobile && <PetsittersSelection ref={petsittersSelectionRef} sx={{ position: 'sticky' }} />} */}

        <Container maxWidth="lg" sx={{ paddingBottom: '54px' }}>
          <EmtSeparator sx={{ margin: '40px 0' }} />
          {/* Benefices */}
          <Benefices />
          {/* END Benefices */}
        </Container>
      </Wrapper>
      {/* END CONTENT */}
      {/* MODAL Carousel */}
      {!isLoading && (
        <ImagesSliderModal
          picturesUrls={allImages}
          initialSlide={imageClickedIndex}
          open={activeModalSlider}
          onClose={() => setActiveModalSlider(false)}
        />
      )}
      {/* END MODAL Carousel */}
      {/* Mobile ask sitter */}
      {isMobile && (
        <MobileSwipeMenu
          ref={swipeMenuRef}
          // title={t('petsitter-profile.mobileSwipeMenu.title', { name: userProfile?.name })}
          // subtitle={t('petsitter-profile.mobileSwipeMenu.subtitle', { name: userProfile?.name })}
          component={
            petsitterPrice ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: '35px 20px 10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                  onClick={() => swipeMenuRef.current?.open()}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Box>
                      <Box>
                        <EmtTypography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {reduction && (
                                <EmtTypography variant="body2" sx={{ textDecoration: 'line-through' }}>
                                  {formatPrice(petsitterPrice.total + reduction)}€
                                </EmtTypography>
                              )}
                              <EmtTypography
                                variant="body2"
                                fontWeight={700}
                                textAlign="center"
                                fontSize="18px"
                                sx={{ color: reduction ? '#18BA92' : 'inherit' }}
                              >
                                {formatPrice(petsitterPrice.total)}€
                              </EmtTypography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PriceIncludedTooltip />
                            </Box>
                          </Box>
                        </EmtTypography>
                        <EmtTypography variant="body2" color="#77757F">
                          {PRODUCTS_MAP[petsitterPrice?.orderDetails?.[0]?.productSlug]?.completeTitle},{' '}
                          {Math.max(1, quantity)} {PRODUCTS_MAP[petsitterPrice?.orderDetails?.[0]?.productSlug]?.title}
                          {Math.max(1, quantity) > 1 ? 's' : ''},{' '}
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {` ${Math.max(getPetsNumber(petsToComplete), 1)} anima${
                              getPetsNumber(petsToComplete) > 1 ? 'ux' : 'l'
                            }`}
                          </span>
                        </EmtTypography>
                      </Box>
                    </Box>
                  </Box>
                  <EmtButton
                    variant="outlined"
                    color="purple"
                    size="small"
                    className="!w-[34px] !h-[34px] !p-0 flex-shrink-0 !min-w-0"
                    sx={{ borderColor: '#E8E8EA' }}
                    onClick={() => {}}
                  >
                    <EmtIcon
                      className="emt-edit"
                      size="small"
                      sx={{ fontSize: '15px' }}
                      onClick={() => swipeMenuRef.current?.open()}
                    />
                  </EmtButton>
                </Box>

                <PetsittersSelectionButtons id={customerId} selectClickOverride={() => setSignUpModalOpen(true)} />

                {petsitterPrice?.cancellationPolicies && (
                  <EmtTypography variant="caption" color="greyGradient.light" fontWeight={400} textAlign="center">
                    <b>Annulation gratuite</b>{' '}
                    {(
                      cancellationPoliciesMapping.find((item) => item.slug === petsitterPrice?.cancellationPolicies)
                        ?.title || ''
                    ).toLowerCase()}
                    .
                  </EmtTypography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '35px 20px 10px',
                }}
              >
                <EmtButton
                  variant="outlined"
                  color="purple"
                  fullWidth
                  sx={{ height: '48px' }}
                  onClick={() => swipeMenuRef.current?.open()}
                >
                  Voir les tarifs
                </EmtButton>
                <PetsittersSelectionButtons id={customerId} compact sx={{ height: '48px', width: '48px' }} />
              </Box>
            )
          }
          isLoading={isLoading}
        >
          {!isLoading && userProfile && (
            <AskSitterCard
              data={{
                ...userProfile,
                reviewsCount: reviewsSummary.reviewsCount,
                reviewsRating: reviewsSummary.averageRating,
              }}
              onScrollToSection={scrollWithOffset}
              shadows={false}
              setSignUpModalOpen={() => setSignUpModalOpen(true)}
            />
          )}
        </MobileSwipeMenu>
      )}
      {/* END Mobile ask sitter */}
      {/* Similars */}{' '}
      {userProfile?.similarProfiles && userProfile?.similarProfiles.length > 0 && (
        <Container maxWidth="lg" sx={{ paddingTop: isMobile ? '0' : '36px', paddingBottom: '36px' }}>
          <Similars data={userProfile.similarProfiles} />
        </Container>
      )}
      {/* END Similars */}
      {/* SIGN UP MODAL */}
      <SignUpModal open={signUpModalOpen} onClose={() => setSignUpModalOpen(false)} />
      <Footer />
    </>
  )
}

export default PublicPetSitterProfileComponent
